import { createClient, type SupabaseClient, type User } from '@supabase/supabase-js';

const ENV = import.meta.env as Record<string, string | undefined>;

let supabase: SupabaseClient | null = null;

export interface AuthUser {
  id: string;
  name: string | null;
}

function toUser(u: User | null): AuthUser | null {
  return u
    ? {
        id: u.id,
        name: (u.user_metadata?.name ?? u.user_metadata?.full_name ?? u.email) ?? null,
      }
    : null;
}

export function getSupabase(): SupabaseClient {
  if (supabase) return supabase;
  if (!ENV.VITE_SUPABASE_URL || !ENV.VITE_SUPABASE_ANON_KEY) {
    throw new Error('Supabase env 未設定 (VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY)');
  }
  supabase = createClient(ENV.VITE_SUPABASE_URL, ENV.VITE_SUPABASE_ANON_KEY, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
    },
  });
  return supabase;
}

export function devBypassUid(): string | null {
  const v = ENV.VITE_AUTH_DEV_BYPASS;
  if (!v) return null;
  return v === '1' ? 'local' : v;
}

export async function signInWithGoogle(): Promise<void> {
  const { error } = await getSupabase().auth.signInWithOAuth({
    provider: 'google',
    options: { redirectTo: window.location.origin },
  });
  if (error) throw error;
}

export async function signOut(): Promise<void> {
  if (devBypassUid()) return;
  const { error } = await getSupabase().auth.signOut();
  if (error) throw error;
}

export async function getAccessToken(): Promise<string | null> {
  const dev = devBypassUid();
  if (dev) return `dev:${dev}`;
  const { data } = await getSupabase().auth.getSession();
  return data.session?.access_token ?? null;
}

export function onAuthChange(cb: (user: AuthUser | null) => void): () => void {
  const dev = devBypassUid();
  if (dev) {
    cb({ id: dev, name: dev });
    return () => {};
  }

  const client = getSupabase();
  const { data } = client.auth.onAuthStateChange((_event, session) => {
    cb(toUser(session?.user ?? null));
  });
  void client.auth
    .getSession()
    .then(({ data: sessionData }) => cb(toUser(sessionData.session?.user ?? null)))
    .catch(() => cb(null));

  return () => data.subscription.unsubscribe();
}
